import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ConfirmOptions {
  title: string;
  body?: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

// Promise-based like window.confirm, so handlers keep the shape
// `if (!(await confirm(...))) return;` while the question looks like the panel.
export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    []
  );

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialog = pending ? (
    <ConfirmDialog
      title={pending.title}
      body={pending.body}
      confirmLabel={pending.confirmLabel}
      danger={pending.danger}
      onClose={close}
    />
  ) : null;

  return { confirm, dialog };
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger = false,
  onClose,
}: ConfirmOptions & { onClose: (ok: boolean) => void }) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // A destructive action waits for a deliberate choice: Enter must not confirm it.
    (danger ? cancelRef : actionRef).current?.focus();
    return () => opener?.focus();
  }, [danger]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose(false);
      return;
    }
    if (e.key !== "Tab") return;
    const first = cancelRef.current;
    const last = actionRef.current;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  // Rendered into <body>: the content area is a size container, and a container
  // becomes the positioning box for fixed elements inside it, so the backdrop
  // would stop covering the sidebar.
  return createPortal(
    <div
      className="confirm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose(false);
      }}
    >
      <div
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        onKeyDown={onKeyDown}
      >
        <h2 id={titleId} className="confirm-title">
          {title}
        </h2>
        {body && (
          <div id={bodyId} className="confirm-body">
            {body}
          </div>
        )}
        <div className="confirm-actions">
          <button
            ref={cancelRef}
            type="button"
            className="mav-btn-secondary"
            onClick={() => onClose(false)}
          >
            Отмена
          </button>
          <button
            ref={actionRef}
            type="button"
            className={danger ? "mav-btn-primary btn-danger" : "mav-btn-primary"}
            onClick={() => onClose(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
